// app/assets/javascripts/application/stores/catalog_state.js

/**
 * @file Хранилище состояния каталога.
 * Содержит активные фильтры, сортировку и пагинацию.
 * Поддерживает паттерн публикации/подписки: подписчики уведомляются при любом изменении.
 * Экспортируется единственный синглтон екземпляра.
 */

const DEFAULT_SORT = 'high-rating'

/**
 * @class
 */
class CatalogState {
  #filters = {}
  #sort = DEFAULT_SORT
  #page = 1
  #pageSize = 12
  #initialPageSize = 12
  #subscribers = new Set()

  /**
   * Возвращает глубокую копию активных фильтров.
   * @returns {Object.<string, *>}
   */
  get filters() {
    return structuredClone(this.#filters)
  }

  /**
   * Возвращает текущий снимок состояния.
   * @returns {{ filters: object, sort: string, page: number, pageSize: number }}
   */
  get snapshot() {
    return this.#snapshot()
  }

  /**
   * Настраивает начальный размер страницы (игнорируется при некорректном значении)
   * и сбрасывает фильтры, сортировку и пагинацию. Вызывается при подключении каталога,
   * чтобы состояние синглтона от предыдущего каталога (Turbo-навигация между
   * страницами клубов и тренеров) не протекало в новый.
   * @param {{ pageSize?: number }} [options={}]
   */
  configure({ pageSize } = {}) {
    this.#filters = {}
    this.#sort = DEFAULT_SORT

    if (Number.isInteger(pageSize) && pageSize >= 1) {
      this.#pageSize = pageSize
      this.#initialPageSize = pageSize
    }

    this.#resetPagination()
  }

  /**
   * Устанавливает активные фильтры и сбрасывает пагинацию.
   * @param {Object.<string, *>} filters
   */
  setFilters(filters) {
    this.#filters = structuredClone(filters)
    this.#resetPagination()
    this.#notify()
  }

  /**
   * Устанавливает активную сортировку и сбрасывает пагинацию.
   * @param {string} sort - Ключ сортировки (например, `"high-rating"`, `"new"`).
   */
  setSort(sort) {
    if (!sort || sort === this.#sort) return

    this.#sort = sort
    this.#resetPagination()
    this.#notify()
  }

  /**
   * Переходит на указанную страницу.
   * @param {number} page
   */
  setPage(page) {
    if (!Number.isInteger(page) || page < 1 || page === this.#page) return

    this.#page = page
    this.#notify()
  }

  /**
   * Увеличивает количество видимых элементов на один шаг (кнопка «Показать ещё»).
   */
  showMore() {
    this.#pageSize += this.#initialPageSize
    this.#page = 1
    this.#notify()
  }

  /**
   * Подписывает каллбэк на изменения состояния.
   * @param {function({ filters: object, sort: string, page: number, pageSize: number }): void} callback
   * @returns {function(): void} Функция для отписки.
   */
  subscribe(callback) {
    this.#subscribers.add(callback)

    return () => this.#subscribers.delete(callback)
  }

  #notify() {
    const snapshot = this.#snapshot()
    this.#subscribers.forEach((callback) => callback(snapshot))
  }

  #resetPagination() {
    this.#page = 1
    this.#pageSize = this.#initialPageSize
  }

  #snapshot() {
    return structuredClone({
      filters: this.#filters,
      sort: this.#sort,
      page: this.#page,
      pageSize: this.#pageSize
    })
  }
}

export { CatalogState }
export default new CatalogState()
